import React, {useState, useEffect} from 'react';
import AppTable from '../../../../Presentation/components/ui/AppTable';
import {AppText, AppBodyText} from '../../../../Presentation/components/ui/Typography';
import {Badge} from '../../../../Presentation/components/ui/Badge';
import Button from '../../../../Presentation/components/ui/Button';
import {Permission, ResourceType, ActionType} from '../../models/permission.model';
import DateTimeDisplay from '../../../../Presentation/components/ui/DateTimeDisplay';
import {Edit, Trash2, Shield} from 'lucide-react';
import {useAuthorization} from '../../../auth/hooks/useAuthorization';
import {PERMISSIONS} from '../../../../constants/permissions';

interface PermissionsTableProps {
  data: Permission[];
  page?: number;
  pageSize?: number;
  totalCount?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onEdit?: (permission: Permission) => void;
  onDelete?: (permissionId: number) => void;
  showPageSizeSelector?: boolean;
  showPaginationInfo?: boolean;
  loading?: boolean;
}

const getResourceDisplayName = (resource: string): string => {
  const resourceNames: Record<string, string> = {
    [ResourceType.USERS]: 'المستخدمين',
    [ResourceType.STORES]: 'المتاجر',
    [ResourceType.PRODUCTS]: 'المنتجات',
    [ResourceType.INVENTORY]: 'المخزون',
    [ResourceType.SALES]: 'المبيعات',
    [ResourceType.DISTRIBUTION]: 'التوزيع',
    [ResourceType.VISITS]: 'الزيارات',
    [ResourceType.BILLING]: 'الفواتير',
    [ResourceType.REPORTS]: 'التقارير',
    [ResourceType.SETTINGS]: 'الإعدادات',
    [ResourceType.PERMISSIONS]: 'الصلاحيات',
    [ResourceType.ROLES]: 'الأدوار',
    [ResourceType.BRANDS]: 'البراندات',
    [ResourceType.DASHBOARD]: 'لوحة التحكم',
    [ResourceType.MOBILE]: 'التطبيق المحمول',
    [ResourceType.SYSTEM]: 'النظام'
  };
  return resourceNames[resource] || resource;
};

const getActionDisplayName = (action: string): string => {
  const actionNames: Record<string, string> = {
    [ActionType.CREATE]: 'إنشاء',
    [ActionType.READ]: 'عرض',
    [ActionType.UPDATE]: 'تعديل',
    [ActionType.DELETE]: 'حذف',
    [ActionType.LIST]: 'قائمة',
    [ActionType.EXPORT]: 'تصدير',
    [ActionType.IMPORT]: 'استيراد',
    [ActionType.APPROVE]: 'موافقة',
    [ActionType.REJECT]: 'رفض',
    [ActionType.ASSIGN]: 'تعيين',
    [ActionType.MANAGE]: 'إدارة'
  };
  return actionNames[action] || action;
};

const getActionBadgeVariant = (action: string): 'success' | 'warning' | 'destructive' | 'default' | 'secondary' => {
  switch (action) {
    case ActionType.CREATE:
      return 'success';
    case ActionType.READ:
      return 'default';
    case ActionType.UPDATE:
      return 'warning';
    case ActionType.DELETE:
      return 'destructive';
    case ActionType.MANAGE:
      return 'secondary';
    default:
      return 'default';
  }
};

export const PermissionsTable: React.FC<PermissionsTableProps> = ({
  data,
  page = 1,
  pageSize = 10,
  totalCount = 0,
  onPageChange,
  onPageSizeChange,
  onEdit,
  onDelete,
  showPageSizeSelector = true,
  showPaginationInfo = true,
  loading = false
}) => {
  const {hasPermission} = useAuthorization();
  const columns = [
    {
      key: 'name',
      title: 'اسم الصلاحية',
      render: (permission: Permission) => (
        <div className="flex items-center space-x-2">
          <Shield className="w-4 h-4 text-brand-500" />
          <AppText className="font-medium">{permission.name}</AppText>
        </div>
      )
    },
    {
      key: 'resource',
      title: 'المورد',
      render: (permission: Permission) => (
        <Badge variant="default" className="text-xs">
          {getResourceDisplayName(permission.resource)}
        </Badge>
      )
    },
    {
      key: 'action',
      title: 'الإجراء',
      render: (permission: Permission) => (
        <Badge variant={getActionBadgeVariant(permission.action)} className="text-xs">
          {getActionDisplayName(permission.action)}
        </Badge>
      )
    },
    {
      key: 'description',
      title: 'الوصف',
      render: (permission: Permission) => (
        <AppBodyText className="text-gray-600 max-w-xs truncate">
          {permission.description || 'لا يوجد وصف'}
        </AppBodyText>
      )
    },
    {
      key: 'createdAt',
      title: 'تاريخ الإنشاء',
      render: (permission: Permission) => (
        permission.createdAt ? (
          <DateTimeDisplay
            dateString={permission.createdAt}
            dateClassName="text-sm text-gray-600"
            timeClassName="text-xs text-gray-500"
          />
        ) : (
          <AppBodyText className="text-gray-500">-</AppBodyText>
        )
      )
    }
  ];

  return (
    <div className="flex-1 min-h-[calc(100vh-200px)] flex flex-col">
      <div className="overflow-x-auto">
        <AppTable
          columns={columns}
          data={data}
          rowKey={(permission) => permission.id.toString()}
          emptyText="لم يتم العثور على صلاحيات"
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          showPageSizeSelector={showPageSizeSelector}
          showPaginationInfo={showPaginationInfo}
          loading={loading}
          actions={(permission) => {
            const actions = [];


            // Edit Permission - Requires system settings permission
            if (onEdit && hasPermission(PERMISSIONS.SYSTEM_SETTINGS)) {
              actions.push({
                label: 'تعديل',
                icon: <Edit className="w-4 h-4" />,
                onClick: () => onEdit(permission),
                variant: 'ghost' as const
              });
            }

            // Delete Permission - Requires system settings permission
            if (onDelete && hasPermission(PERMISSIONS.SYSTEM_SETTINGS)) {
              actions.push({
                label: 'حذف',
                icon: <Trash2 className="w-4 h-4" />,
                onClick: () => onDelete(permission.id),
                variant: 'danger' as const
              });
            }

            return actions;
          }}
        />
      </div>
    </div>
  );
};
